import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import {
    SidebarMenuItem,
    SidebarMenuButton,
    SidebarMenuSub,
    SidebarMenuSubItem,
    SidebarMenuSubButton,
    useSidebar,
} from 'src/shared/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu';

export type NestedSidebarMenuLeafItem = {
    title: string;
    testId?: string;
    onClick: () => void;
    isActive?: boolean;
};

export type NestedSidebarMenuGroupItem = {
    title: string;
    testId?: string;
    subItems: NestedSidebarMenuLeafItem[];
};

export type NestedSidebarMenuSubItem = NestedSidebarMenuLeafItem | NestedSidebarMenuGroupItem;

export const isSubGroup = (item: NestedSidebarMenuSubItem): item is NestedSidebarMenuGroupItem => 'subItems' in item;

type NestedSidebarMenuItemProps = {
    label: string;
    testId?: string;
    icon: React.ReactNode;
    isActive: boolean;
    subItems: NestedSidebarMenuSubItem[];
    defaultOpen?: boolean;
    expandedButtonClassName?: string;
    collapsedButtonClassName?: string;
    labelClassName?: string;
};

const NestedSidebarMenuSubGroup = ({ item }: { item: NestedSidebarMenuGroupItem }) => {
    const isActive = item.subItems.some(subItem => subItem.isActive);
    const [open, setOpen] = useState(isActive);
    const isFirstRender = useRef(true);
    const { pathname } = useLocation();

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setOpen(isActive);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    return (
        <SidebarMenuSubItem>
            <Collapsible open={open} onOpenChange={setOpen} className='group/nested-collapsible'>
                <CollapsibleTrigger asChild>
                    <SidebarMenuSubButton asChild isActive={isActive && !open} data-testid={item.testId}>
                        <button type='button' className='flex w-full items-center gap-2'>
                            <span>{item.title}</span>
                            <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/nested-collapsible:rotate-90' />
                        </button>
                    </SidebarMenuSubButton>
                </CollapsibleTrigger>
                <CollapsibleContent className='overflow-hidden pr-1 data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up'>
                    <SidebarMenuSub className='mr-0 pr-0'>
                        {item.subItems.map(subItem => (
                            <SidebarMenuSubItem key={subItem.title}>
                                <SidebarMenuSubButton asChild isActive={subItem.isActive}>
                                    <button onClick={subItem.onClick} className='w-full' data-testid={subItem.testId}>
                                        <span>{subItem.title}</span>
                                    </button>
                                </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                        ))}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </Collapsible>
        </SidebarMenuSubItem>
    );
};

const DropdownMenuSubGroupItem = ({ item }: { item: NestedSidebarMenuGroupItem }) => (
    <DropdownMenuSub>
        <DropdownMenuSubTrigger
            data-testid={item.testId}
            data-active={item.subItems.some(subItem => subItem.isActive)}
            className='data-[active=true]:bg-sidebar-accent data-[active=true]:text-foreground'
        >
            <span>{item.title}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
            {item.subItems.map(subItem => (
                <DropdownMenuItem
                    key={subItem.title}
                    onClick={subItem.onClick}
                    data-testid={subItem.testId}
                    data-active={subItem.isActive}
                    className='focus:bg-secondary focus:text-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-foreground'
                >
                    <span>{subItem.title}</span>
                </DropdownMenuItem>
            ))}
        </DropdownMenuSubContent>
    </DropdownMenuSub>
);

// SidebarMenuItem (<li>) is always the outermost element here, with the
// Collapsible/DropdownMenu wrapper nested inside it. This keeps SidebarMenu
// (<ul>) valid HTML — it only ever has <li> children, never a <div> sibling.
const NestedSidebarMenuItem = ({
    label,
    testId,
    icon,
    isActive,
    subItems,
    defaultOpen,
    expandedButtonClassName,
    collapsedButtonClassName,
    labelClassName,
}: NestedSidebarMenuItemProps) => {
    const { state, isMobile } = useSidebar();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(defaultOpen ?? false);
    const isFirstRender = useRef(true);

    // Respect defaultOpen on mount (even if isActive is already true then); only sync open state to
    // isActive afterwards. Keyed on pathname rather than isActive itself: a group opened manually
    // while inactive (e.g. browsing) never has an isActive transition to react to, so it would
    // otherwise stay open forever once navigation moves on to unrelated pages.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setOpen(isActive);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    if (state === 'collapsed' && !isMobile) {
        return (
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton tooltip={label} isActive={isActive} data-testid={testId} className={collapsedButtonClassName}>
                            {icon}
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side='right' align='start'>
                        <DropdownMenuLabel>{label}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            {subItems.map(item =>
                                isSubGroup(item) ? (
                                    <DropdownMenuSubGroupItem key={item.title} item={item} />
                                ) : (
                                    <DropdownMenuItem
                                        key={item.title}
                                        onClick={item.onClick}
                                        data-testid={item.testId}
                                        data-active={item.isActive}
                                        className='focus:bg-secondary focus:text-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-foreground'
                                    >
                                        <span>{item.title}</span>
                                    </DropdownMenuItem>
                                )
                            )}
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        );
    }

    return (
        <SidebarMenuItem>
            <Collapsible open={open} onOpenChange={setOpen} className='group/collapsible'>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                        tooltip={label}
                        isActive={isActive && !open}
                        data-testid={testId}
                        className={expandedButtonClassName}
                    >
                        {icon}
                        <span className={labelClassName}>{label}</span>
                        <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
                    </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent className='overflow-hidden pr-1 data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up'>
                    <SidebarMenuSub className='mr-0 pr-0'>
                        {subItems.map(item =>
                            isSubGroup(item) ? (
                                <NestedSidebarMenuSubGroup key={item.title} item={item} />
                            ) : (
                                <SidebarMenuSubItem key={item.title}>
                                    <SidebarMenuSubButton asChild isActive={item.isActive}>
                                        <button onClick={item.onClick} className='w-full' data-testid={item.testId}>
                                            <span>{item.title}</span>
                                        </button>
                                    </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                            )
                        )}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </Collapsible>
        </SidebarMenuItem>
    );
};

export default NestedSidebarMenuItem;
